/**
 * Whether a `message` event actually came from the Ash iframe.
 *
 * `window.addEventListener('message')` hears every frame on the page, not just
 * ours. Member pages embed frames we don't control — Contentful-authored
 * videos, help videos, Stripe payment fields — and any framed origin can post
 * whatever it likes to its parent. The hosts previously filtered only on the
 * shape of `data`, which any sender can imitate.
 *
 * This became worth closing because `OPEN_MEMBER_CARE_CHAT` reaches a human:
 * the host opens the Genesys Messenger and writes the `ashSessionId` custom
 * attribute a Member Care agent reads, so an unchecked sender can summon an
 * agent and choose the conversation they go looking at.
 *
 * **Inert when the assistant URL isn't configured.** There is nothing to
 * compare against, so the check passes everything through rather than silently
 * breaking every message. That is not a hole in practice: with no `sourceUrl`
 * the iframe has nothing to load, so Ash is already non-functional in that
 * environment (it is the known cause of Ash showing "Just a sec" forever). It
 * also keeps the host test suites working, which dispatch synthetic events with
 * no origin.
 *
 * **Also inert when the configured URL won't parse.** A malformed URL is a
 * deployment problem, not a reason to drop every message and take Ash down with
 * it. Because that reverts to the old trust-everything behaviour, callers
 * should pair this with {@link isAssistantUrlMalformed} and report it once —
 * this module can't see which env var supplied the value, so it can't say
 * anything useful about it, but the caller can.
 *
 * @param eventOrigin - `event.origin` from the message event.
 * @param sourceUrl - The configured assistant URL.
 * @returns Whether to trust the message.
 */
export const isFromAssistant = (
  eventOrigin: string,
  sourceUrl?: string,
): boolean => {
  if (!sourceUrl) return true

  try {
    return eventOrigin === new URL(sourceUrl).origin
  } catch {
    return true
  }
}

/**
 * Whether a configured assistant URL is set but unparseable — the case that
 * silently turns {@link isFromAssistant} off.
 *
 * A value like `ash.sidecarhealth.com` (no scheme) throws in `new URL`, so the
 * origin check fails open and the environment quietly goes back to trusting
 * every frame on the page. Nothing about that is visible from the outside, so
 * hosts log it once at start-up naming the env var they read it from.
 *
 * An _absent_ URL is not malformed: it's a separate, already-diagnosed
 * condition (Ash can't load at all), and reporting it as a bad deploy would be
 * noise in every environment that simply doesn't run Ash.
 *
 * @param sourceUrl - The configured assistant URL.
 * @returns Whether the URL is set but won't parse.
 */
export const isAssistantUrlMalformed = (sourceUrl?: string): boolean => {
  if (!sourceUrl) return false

  try {
    new URL(sourceUrl)
    return false
  } catch {
    return true
  }
}
